import { AxiosInstance } from 'axios'
import { BaseHTTP } from './base.http'

export interface CreatePaymentRequest {
  transactionAmount: number
  description?: string
  paymentMethodId?: string
  payer: {
    email: string
    firstName?: string
    lastName?: string
    identification?: {
      type: string
      number: string
    }
  }
  installments: number
  token: string
  issuerId?: number
}

export interface PaymentResponse {
  id: number
  dateCreated: string
  dateApproved?: string
  dateLastUpdated: string
  status: string
  statusDetail: string
  transactionAmount: number
  currencyId: string
  description?: string
  paymentMethodId?: string
  card?: {
    lastFourDigits: string
  }
}

export interface SaveCardRequest {
  transactionAmount: number
  description: string
  paymentMethodId: string
  payer: {
    email: string
    firstName?: string
    lastName?: string
    identification?: {
      type: string
      number: string
    }
  }
  token: string
  installments?: number
  issuerId?: number
  cardName?: string
}

export interface SavedCard {
  id: number
  organizationId: number
  provider: string
  mercadopagoCardId?: string | null
  lastFourDigits: string
  cardType: string
  paymentTypeId?: string | null
  name: string
  createdAt: string
  updatedAt: string
}

export interface ChargeRequest {
  amount: number
  description: string
}

export interface RefundRequest {
  amount?: number
}

export interface SubscriptionCheckResponse {
  hasActiveSubscription: boolean
  daysRemaining: number | null
  hasCard: boolean
  hasDebt: boolean
  daysUntilBlock: number | null
}

export class PaymentAPI {
  private http!: AxiosInstance

  constructor() {
    this.http = BaseHTTP.getInstance().http
  }

  createPayment = async (paymentData: CreatePaymentRequest) => {
    const path = 'v1/payment'
    return this.http.post<PaymentResponse>(path, paymentData)
  }

  saveCard = async (cardData: SaveCardRequest) => {
    const path = 'v1/payment/save-card'
    return this.http.post<SavedCard>(path, cardData)
  }

  chargeCard = async (cardId: number, chargeData: ChargeRequest) => {
    const path = `v1/payment/${cardId}/charge`
    return this.http.post<PaymentResponse>(path, chargeData)
  }

  refundPayment = async (cardId: number, paymentId: number, refundData?: RefundRequest) => {
    const path = `v1/payment/${cardId}/refund/${paymentId}`
    return this.http.post<PaymentResponse>(path, refundData || {})
  }

  updateCard = async (cardId: number, name: string) => {
    const path = `v1/payment/${cardId}`
    return this.http.patch<SavedCard>(path, { name })
  }

  canDeleteCard = async (cardId: number) => {
    const path = `v1/payment/card/${cardId}/can-delete`
    const res = await this.http.get(path)
    return res.data as {
      canDelete: boolean
      subscriptionIds?: number[]
      addonSubscriptionIds?: number[]
      message?: string
    }
  }

  deleteCard = async (cardId: number) => {
    const path = `v1/payment/${cardId}`
    const res = await this.http.delete(path)
    return res.data as { success: boolean; subscriptionIds?: number[]; addonSubscriptionIds?: number[]; message?: string }
  }

  updateSubscriptionCard = async (
    organizationId: number,
    subscriptionId: number,
    body: SaveCardRequest
  ) => {
    const path = `v1/payment/organization/${organizationId}/subscription/${subscriptionId}/card`
    return this.http.put(path, body)
  }

  updateAddonSubscriptionCard = async (
    organizationId: number,
    addonSubscriptionId: number,
    body: SaveCardRequest
  ) => {
    const path = `v1/payment/organization/${organizationId}/addon-subscription/${addonSubscriptionId}/card`
    return this.http.put(path, body)
  }

  getCards = async () => {
    const path = 'v1/payment/cards'
    return this.http.get<SavedCard[]>(path)
  }

  createPlan = async (planData: CreatePlanRequest) => {
    const path = 'v1/payment/plan'
    return this.http.post<Plan>(path, planData)
  }

  getPlans = async () => {
    const path = 'v1/payment/plans'
    return this.http.get<PlansResponse>(path)
  }

  updateOrganizationPlan = async (planId: number) => {
    const path = 'v1/organizations/plan'
    return this.http.patch(path, { planId })
  }

  updateOrganizationPlanWithCard = async (organizationId: number, planData: UpdateOrganizationPlanRequest) => {
    const path = `v1/payment/organization/${organizationId}/plan`
    return this.http.put(path, planData)
  }

  createPlanSubscription = async (organizationId: number, data: CreatePlanSubscriptionRequest) => {
    const path = `v1/payment/organization/${organizationId}/plan-subscription`
    return this.http.post<CreatePlanSubscriptionResponse>(path, data)
  }

  confirmPlanSubscription = async (organizationId: number, subscriptionId: number, data: ConfirmPlanSubscriptionRequest) => {
    const path = `v1/payment/organization/${organizationId}/plan-subscription/${subscriptionId}/confirm`
    return this.http.post<ConfirmPlanSubscriptionResponse>(path, data)
  }

  getOrganizationSubscriptions = async (organizationId: number) => {
    const path = `v1/payment/organization/${organizationId}/plans-subscriptions`
    const params = {
      showOnlyUpgrades: true
    }
    return this.http.get<OrganizationSubscriptionsResponse>(path, { params })
  }

  cancelOrganizationPlan = async (organizationId: number) => {
    const path = `v1/payment/organization/${organizationId}/plan`
    return this.http.delete(path)
  }

  resumeOrganizationPlan = async (organizationId: number, body: ResumePlanRequest) => {
    const path = `v1/payment/organization/${organizationId}/plan/resume`
    return this.http.post(path, body)
  }

  getPayments = async () => {
    const path = 'v1/payment'
    return this.http.get<PaymentEntity[]>(path)
  }

  getAddons = async () => {
    const path = 'v1/payment/addons'
    return this.http.get<AddonsResponse>(path)
  }

  getAddonSubscriptions = async (organizationId: number) => {
    const path = `v1/payment/organization/${organizationId}/addon-subscriptions`
    return this.http.get<AddonSubscription[]>(path)
  }

  checkOrganizationSubscription = async (organizationId: number) => {
    const path = `v1/payment/organization/${organizationId}/check-subscription`
    return this.http.get<SubscriptionCheckResponse>(path)
  }

  createAddonSubscription = async (organizationId: number, data: CreateAddonSubscriptionRequest) => {
    const path = `v1/payment/organization/${organizationId}/addon-subscription`
    return this.http.post<CreateAddonSubscriptionResponse>(path, data)
  }

  confirmAddonSubscription = async (
    organizationId: number,
    addonSubscriptionId: number,
    data: ConfirmAddonSubscriptionRequest
  ) => {
    const path = `v1/payment/organization/${organizationId}/addon-subscription/${addonSubscriptionId}/confirm`
    return this.http.post<ConfirmAddonSubscriptionResponse>(path, data)
  }
}

export interface Addon {
  id: number
  name: string
  description: string
  ufPriceWithoutTaxes: number
  addonType: string
  type?: string
  affectedPermission?: string | string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface AddonSubscription {
  id: number
  subscriptionId: number
  addonId: number
  cardId: number
  extraBusinessesAmount?: number
  extraBanksAmount?: number
  extraAccounting?: boolean
  extraPaymentMethods?: boolean
  paymentDate?: string | null
  nextPaymentDate?: string | null
  isExpired: boolean
  isActive: boolean
  paymentPending?: boolean
  createdAt: string
  updatedAt: string
  addon?: Addon
  subscription?: { id: number; planId: number; cardId: number; isActive: boolean; isExpired: boolean }
  card?: { id: number; name: string; lastFourDigits: string }
}

export interface AddonSubscriptionItem {
  addonId: number
  quantity: number
}

export interface CreateAddonSubscriptionRequest {
  addons: AddonSubscriptionItem[]
  cardId: number
  token: string
  payerEmail?: string
}

export interface CreateAddonSubscriptionResponse {
  addonSubscriptionIds: number[]
  addonSubscriptions?: AddonSubscription[]
  paymentPendingReviewManual?: boolean
}

export interface ConfirmAddonSubscriptionRequest {
  token: string
}

export interface ConfirmAddonSubscriptionResponse {
  addonSubscription: AddonSubscription
  addonSubscriptions?: AddonSubscription[]
  paymentPendingReviewManual?: boolean
}

export interface CreatePlanRequest {
  reason: string
  backUrl: string
  frequency: string
  description?: string
  ufPriceWithoutTaxes?: number
  freeTrialDays?: number
}

export interface Plan {
  id: number
  name: string
  description?: string | null
  maxTokens: number
  maxBusinesses: number
  maxMovements: number
  maxBanks: number
  allowMultiUsers: boolean
  allowedSupports: string[]
  allowedReports: string[]
  allowedDashboards: string[]
  allowBukIntegration: boolean
  allowMercadopagoIntegration: boolean
  allowGetnetMovements: boolean
  allowTransbankMovements: boolean
  allowCostCenter: boolean
  allowIntelligentBilling: boolean
  allowAccountingDesk: boolean
  ufPriceWithoutTaxes?: number | null
  freeTrialDays?: number | null
  externalId?: string | null
  frequency: string
  paymentTypeId?: string | null
  isPrivate?: boolean
  includedFeatures?: string[] | null
  upgradeDiscountUf?: number
  /** Sin tarjetas registradas; plan sigue asignado hasta fin de período */
  isCancelled?: boolean
}

/** POST .../plan/resume — preaprobación recurrente con tarjeta ya guardada (no crea tarjeta ni cobro inicial). */
export interface ResumePlanRequest {
  cardId: number
  token: string
  preapprovalToken?: string
  flowId?: string
  payerEmail?: string
}

export interface UpdateOrganizationPlanRequest {
  planId: number
  cardId: number
  token: string
  externalReference?: string
}

export interface CreatePlanSubscriptionRequest {
  planId: number
  cardId: number
  token: string
  payerEmail?: string
}

export interface ConfirmPlanSubscriptionRequest {
  token: string
}

export interface CreatePlanSubscriptionResponse {
  subscriptionId: number
  subscription: Subscription
  paymentPendingReviewManual?: boolean
}

export interface ConfirmPlanSubscriptionResponse {
  paymentPendingReviewManual?: boolean
  subscription?: Subscription
}

export interface UpdateOrganizationPlanResponse {
  organization: {
    id: number
    name: string
    active: boolean
    planId: number
    createdAt: string
    updatedAt: string
  }
  newSubscription: {
    id: number
    cardId: number
    planId: number
    externalId: string
    metadata: Record<string, any>
    createdAt: string
    updatedAt: string
  }
}

export interface Subscription {
  id: number
  cardId: number
  planId: number
  externalId: string
  metadata: Record<string, any>
  startDate: string
  endDate?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  plan?: Plan
}

export interface PlansResponse {
  plans: Plan[]
  ufValue: number
}

export interface AddonsResponse {
  addons: Addon[]
  ufValue: number
}

export interface OrganizationSubscriptionsResponse {
  plans: Plan[]
  subscriptions: Subscription[]
  addonSubscriptions?: AddonSubscription[]
  currentPlan: Plan | null
  ufValue: number
}

export interface PaymentEntity {
  id: number
  cardId: number
  externalId: string
  amount: string
  description: string
  metadata: Record<string, any>
  createdAt: string
  updatedAt: string
  card?: SavedCard
}

export default new PaymentAPI()
